import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { useProperties } from "@/contexts/PropertyContext";
import { Modal, Field } from "./Rooms";
import {
    Plus, Camera, X, AlertTriangle, RefreshCw, Sparkles, ListFilter,
    User, Clock, Image as ImageIcon, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime, formatTime } from "@/lib/format";

// 4-column workflow: pending → in_progress → inspected → done. (skipped hidden in stats)
const COL_KEYS = ["pending", "in_progress", "inspected", "done"];
const COL_TONE = {
    pending: "bg-muted/40 border-border",
    in_progress: "bg-blue-50 border-blue-100",
    inspected: "bg-amber-50 border-amber-100",
    done: "bg-emerald-50 border-emerald-100",
};

const PRIORITY_RANK = { high: 3, normal: 2, low: 1 };

const TAB_KEYS = ["kanban", "today", "rooms"];

const POLL_MS = 15000;

export default function Housekeeping() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { properties, activePropertyId } = useProperties();
    const isHK = user?.role === "housekeeping";
    const canManage = ["hotel_admin", "property_manager", "super_admin"].includes(user?.role);

    const [tab, setTab] = useState(isHK ? "today" : "kanban");
    const [tasks, setTasks] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [staff, setStaff] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState({ priority: "", assigned_to: "" });
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const pollRef = useRef(null);

    const load = async (silent = false) => {
        const params = new URLSearchParams();
        if (activePropertyId !== "all") params.set("property_id", activePropertyId);
        if (filter.priority) params.set("priority", filter.priority);
        if (filter.assigned_to) params.set("assigned_to", filter.assigned_to);
        try {
            const [t, r, s] = await Promise.all([
                api.get(`/housekeeping/tasks?${params}`),
                api.get(`/rooms?${activePropertyId !== "all" ? `property_id=${activePropertyId}` : ""}`),
                api.get("/housekeeping/stats"),
            ]);
            setTasks(t.data); setRooms(r.data); setStats(s.data);
            setLastUpdate(new Date());
            if (!silent) {/* user-initiated */}
        } catch (e) {
            if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const loadStaff = async () => {
        if (isHK) return;
        try {
            const { data } = await api.get("/users");
            setStaff(data.filter((u) => ["housekeeping", "property_manager", "hotel_admin"].includes(u.role)));
        } catch { /* noop */ }
    };

    useEffect(() => { load(); loadStaff(); /* eslint-disable-next-line */ }, [activePropertyId, filter.priority, filter.assigned_to]);

    // Polling for realtime updates
    useEffect(() => {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(() => load(true), POLL_MS);
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line
    }, [activePropertyId, filter.priority, filter.assigned_to]);

    const setStatus = async (task, status) => {
        try {
            await api.patch(`/housekeeping/tasks/${task.id}`, { status });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const autoGenerate = async () => {
        try {
            const params = activePropertyId !== "all" ? `?property_id=${activePropertyId}` : "";
            const { data } = await api.post(`/housekeeping/auto-generate${params}`);
            toast.success(t("housekeeping.auto_generated_toast", { n: data.created }));
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);

    const visibleTabKeys = isHK ? TAB_KEYS.filter((k) => k !== "kanban") : TAB_KEYS;

    return (
        <>
            <Topbar
                title={t("housekeeping.title")}
                subtitle={statsSubtitle(stats, lastUpdate, t)}
                actions={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => load()}
                            className="akm-btn-ghost"
                            data-testid="hk-refresh"
                            title={t("housekeeping.refresh")}
                        >
                            <RefreshCw size={14} />
                        </button>
                        {canManage && (
                            <button onClick={autoGenerate} className="akm-btn-ghost" data-testid="hk-auto-generate">
                                <Wand2 size={14} /> {t("housekeeping.auto_generate")}
                            </button>
                        )}
                        {!isHK && (
                            <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="new-task-btn">
                                <Plus size={14} /> {t("housekeeping.new_task")}
                            </button>
                        )}
                    </div>
                }
            />
            <div className="flex-1 overflow-auto p-4 md:p-6 space-y-4">
                {stats && <StatsRow stats={stats} />}

                <div className="flex items-center gap-1 border-b border-border">
                    {visibleTabKeys.map((k) => (
                        <button key={k} onClick={() => setTab(k)}
                                data-testid={`hk-tab-${k}`}
                                className={`-mb-px border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${
                                    tab === k ? "border-[#ff7b02] text-[#ff7b02]" : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}>
                            {t(`housekeeping.tabs.${k}`)}
                        </button>
                    ))}
                    <div className="ml-auto flex items-center gap-2 pb-2">
                        <select className="akm-input text-[11px] max-w-[140px]" value={filter.priority}
                                onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
                                data-testid="hk-filter-priority">
                            <option value="">{t("housekeeping.all_priorities")}</option>
                            <option value="high">{t("housekeeping.priority_high")}</option>
                            <option value="normal">{t("housekeeping.priority_normal")}</option>
                            <option value="low">{t("housekeeping.priority_low")}</option>
                        </select>
                        {!isHK && (
                            <select className="akm-input text-[11px] max-w-[180px]" value={filter.assigned_to}
                                    onChange={(e) => setFilter({ ...filter, assigned_to: e.target.value })}
                                    data-testid="hk-filter-assignee">
                                <option value="">{t("housekeeping.all_assignees")}</option>
                                <option value="__unassigned__" disabled>{t("housekeeping.unassigned")}</option>
                                {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        )}
                    </div>
                </div>

                {tab === "kanban" && <KanbanBoard tasks={tasks} setStatus={setStatus} roomById={roomById} onOpen={setDetail} isHK={isHK} />}
                {tab === "today" && <TodayList tasks={tasks} setStatus={setStatus} roomById={roomById} onOpen={setDetail} isHK={isHK} />}
                {tab === "rooms" && <RoomsView rooms={rooms} tasks={tasks} />}
            </div>

            {open && (
                <TaskForm rooms={rooms} properties={properties} staff={staff}
                          onClose={() => setOpen(false)}
                          onSaved={async () => { setOpen(false); await load(); }} />
            )}
            {detail && (
                <TaskDrawer
                    task={detail}
                    rooms={rooms}
                    staff={staff}
                    isHK={isHK}
                    onClose={() => setDetail(null)}
                    onReload={async () => { await load(); }}
                />
            )}
        </>
    );
}

function statsSubtitle(stats, lastUpdate, t) {
    if (!stats) return t("housekeeping.loading");
    const total = stats.total_tasks || 0;
    const pending = stats.tasks_by_status?.pending || 0;
    const done = stats.tasks_by_status?.done || 0;
    const high = stats.tasks_by_priority?.high || 0;
    const last = lastUpdate ? t("housekeeping.updated_at", { time: formatTime(lastUpdate) }) : "";
    return t("housekeeping.subtitle_stats", { total, pending, done, high, last });
}

// ------------------------------------------------------------
// Stats row (KPIs)
// ------------------------------------------------------------
function StatsRow({ stats }) {
    const { t } = useTranslation();
    const items = [
        { k: t("housekeeping.stats.pending"), v: stats.tasks_by_status?.pending || 0, color: "text-foreground" },
        { k: t("housekeeping.stats.in_progress"), v: stats.tasks_by_status?.in_progress || 0, color: "text-blue-700" },
        { k: t("housekeeping.stats.inspected"), v: stats.tasks_by_status?.inspected || 0, color: "text-amber-700" },
        { k: t("housekeeping.stats.done_today"), v: stats.tasks_by_status?.done || 0, color: "text-emerald-700" },
        { k: t("housekeeping.stats.high_priority"), v: stats.tasks_by_priority?.high || 0, color: "text-red-700" },
    ];
    return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5" data-testid="hk-stats">
            {items.map((it) => (
                <div key={it.k} className="akm-card p-3">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{it.k}</div>
                    <div className={`mt-1 text-2xl font-semibold tracking-tight ${it.color}`}>{it.v}</div>
                </div>
            ))}
        </div>
    );
}

// ------------------------------------------------------------
// Kanban board (4 columns)
// ------------------------------------------------------------
function KanbanBoard({ tasks, setStatus, roomById, onOpen, isHK }) {
    const { t } = useTranslation();
    const byStatus = (s) => tasks
        .filter((task) => task.status === s)
        .sort((a, b) => (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0));

    return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid="hk-kanban">
            {COL_KEYS.map((colKey) => (
                <div key={colKey} className={`rounded-[10px] border ${COL_TONE[colKey]} p-3`} data-testid={`hk-col-${colKey}`}>
                    <div className="mb-3 flex items-center justify-between">
                        <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t(`housekeeping.columns.${colKey}`)}</div>
                        <div className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                            {byStatus(colKey).length}
                        </div>
                    </div>
                    <div className="space-y-2">
                        {byStatus(colKey).map((task) => (
                            <TaskCard key={task.id} task={task} room={roomById[task.room_id]}
                                      setStatus={setStatus} onOpen={onOpen} isHK={isHK} />
                        ))}
                        {byStatus(colKey).length === 0 && (
                            <div className="py-6 text-center text-[11px] text-muted-foreground">{t("housekeeping.no_tasks")}</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ------------------------------------------------------------
// Today list (compact, mobile-friendly)
// ------------------------------------------------------------
function TodayList({ tasks, setStatus, roomById, onOpen, isHK }) {
    const today = new Date().toISOString().slice(0, 10);
    const items = tasks
        .filter((t) => t.status !== "done" && t.status !== "skipped")
        .filter((t) => !t.due_date || t.due_date <= today)
        .sort((a, b) => (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0));
    return (
        <div className="space-y-2" data-testid="hk-today">
            {items.map((t) => (
                <TaskCard key={t.id} task={t} room={roomById[t.room_id]}
                          setStatus={setStatus} onOpen={onOpen} isHK={isHK} dense />
            ))}
            {items.length === 0 && (
                <div className="akm-card p-10 text-center text-sm text-muted-foreground">
                    <Sparkles className="mx-auto mb-2" size={20} />
                    All caught up. Nothing pending today.
                </div>
            )}
        </div>
    );
}

// ------------------------------------------------------------
// Rooms view (live status snapshot for reception)
// ------------------------------------------------------------
const ROOM_STATUSES = ["clean", "dirty", "occupied", "maintenance", "blocked", "out_of_order"];
function RoomsView({ rooms, tasks }) {
    const [filter, setFilter] = useState("all");
    const visible = filter === "all" ? rooms : rooms.filter((r) => r.status === filter);
    const taskByRoom = useMemo(() => {
        const m = {};
        for (const t of tasks) {
            if (!m[t.room_id] || PRIORITY_RANK[t.priority] > PRIORITY_RANK[m[t.room_id].priority]) {
                m[t.room_id] = t;
            }
        }
        return m;
    }, [tasks]);
    return (
        <div className="space-y-3" data-testid="hk-rooms">
            <div className="flex flex-wrap gap-1">
                <button onClick={() => setFilter("all")}
                        className={`rounded-md border px-3 py-1.5 text-[12px] font-medium ${filter === "all" ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]" : "border-border text-muted-foreground hover:bg-muted/40"}`}>
                    All ({rooms.length})
                </button>
                {ROOM_STATUSES.map((s) => {
                    const n = rooms.filter((r) => r.status === s).length;
                    return (
                        <button key={s} onClick={() => setFilter(s)}
                                className={`rounded-md border px-3 py-1.5 text-[12px] font-medium capitalize ${filter === s ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]" : "border-border text-muted-foreground hover:bg-muted/40"}`}>
                            {s.replace(/_/g, " ")} ({n})
                        </button>
                    );
                })}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7">
                {visible.map((r) => {
                    const t = taskByRoom[r.id];
                    return (
                        <div key={r.id} className="akm-card p-3" data-testid={`hk-room-${r.number}`}>
                            <div className="flex items-start justify-between">
                                <div className="text-sm font-semibold">{r.number}</div>
                                <StatusBadge status={r.status} />
                            </div>
                            <div className="mt-1 text-[11px] text-muted-foreground capitalize">{r.room_type}</div>
                            {t && (
                                <div className="mt-2 rounded-md bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
                                    {t.priority === "high" && <AlertTriangle className="mr-1 inline" size={10} />}
                                    {t.status.replace("_", " ")} · {t.task_type}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Task card
// ------------------------------------------------------------
function TaskCard({ task, room, setStatus, onOpen, isHK, dense }) {
    const nextStates = nextStatesFor(task.status);
    const priorityBg = task.priority === "high"
        ? "bg-red-50 border-red-200"
        : task.priority === "low"
        ? "bg-card border-border"
        : "bg-card border-border";

    return (
        <div className={`rounded-md border ${priorityBg} p-3 transition hover:shadow-sm cursor-pointer`}
             onClick={() => onOpen(task)}
             data-testid={`hk-task-${task.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold">Room {room?.number || "—"}</div>
                        {task.priority === "high" && (
                            <span className="rounded-full bg-red-100 px-1.5 py-0 text-[10px] font-medium text-red-700">HIGH</span>
                        )}
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground capitalize">
                        {task.task_type.replace("_", " ")}{room ? ` · ${room.room_type}` : ""}
                    </div>
                </div>
                <StatusBadge status={task.status} />
            </div>

            {!dense && task.notes && (
                <div className="mt-2 line-clamp-2 text-[11px] text-muted-foreground">{task.notes}</div>
            )}

            <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <div className="flex items-center gap-1 truncate">
                    <User size={10} /> {task.assigned_to_name || "Unassigned"}
                </div>
                <div className="flex items-center gap-2">
                    {task.photos?.length > 0 && (
                        <span className="inline-flex items-center gap-0.5"><ImageIcon size={10} /> {task.photos.length}</span>
                    )}
                    {task.due_date && <span className="inline-flex items-center gap-0.5"><Clock size={10} /> {task.due_date}</span>}
                </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-1">
                {nextStates.map((s) => (
                    <button key={s} onClick={(e) => { e.stopPropagation(); setStatus(task, s); }}
                            data-testid={`hk-set-${task.id}-${s}`}
                            className="rounded-md border border-border bg-card px-2 py-0.5 text-[11px] capitalize hover:bg-muted/60">
                        → {s.replace("_", " ")}
                    </button>
                ))}
            </div>
        </div>
    );
}

function nextStatesFor(current) {
    switch (current) {
        case "pending": return ["in_progress", "skipped"];
        case "in_progress": return ["inspected", "done", "skipped"];
        case "inspected": return ["done"];
        case "done": return [];
        case "skipped": return ["pending"];
        default: return [];
    }
}

// ------------------------------------------------------------
// Task drawer (details, photos, reassignment, status changes)
// ------------------------------------------------------------
function TaskDrawer({ task, rooms, staff, isHK, onClose, onReload }) {
    const room = rooms.find((r) => r.id === task.room_id);
    const [t, setT] = useState(task);
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/housekeeping/tasks?status=`);
            const fresh = data.find((x) => x.id === task.id);
            if (fresh) setT(fresh);
        } catch { /* noop */ }
    };

    const update = async (patch) => {
        try {
            const { data } = await api.patch(`/housekeeping/tasks/${task.id}`, patch);
            setT(data);
            await onReload();
            toast.success("Updated");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    // ESC to close
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const uploadPhoto = async (file) => {
        if (!file) return;
        if (file.size > 800 * 1024) {
            toast.error("Photo must be under 800KB");
            return;
        }
        const reader = new FileReader();
        reader.onload = async () => {
            setUploading(true);
            try {
                await api.post(`/housekeeping/tasks/${task.id}/photos`, { photo: reader.result });
                toast.success("Photo uploaded");
                await refresh();
                await onReload();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            finally { setUploading(false); }
        };
        reader.readAsDataURL(file);
    };

    const removePhoto = async (idx) => {
        try {
            await api.delete(`/housekeeping/tasks/${task.id}/photos/${idx}`);
            await refresh();
            await onReload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const nextStates = nextStatesFor(t.status);

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="hk-drawer">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className="relative h-full w-full max-w-[560px] overflow-y-auto bg-background shadow-xl">
                <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-5 py-3.5 backdrop-blur">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold">Room {room?.number || "—"} · <span className="capitalize">{t.task_type.replace("_", " ")}</span></div>
                            <div className="text-[11px] text-muted-foreground">
                                {room?.room_type} · {t.priority} priority · {t.due_date || "no due date"}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <StatusBadge status={t.status} />
                            <button onClick={onClose} className="akm-btn-ghost text-[11px]">Close</button>
                        </div>
                    </div>
                </div>

                <div className="space-y-4 px-5 py-4">
                    {/* Status workflow */}
                    <div>
                        <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Status workflow</div>
                        <div className="flex flex-wrap gap-1">
                            {nextStates.map((s) => (
                                <button key={s} onClick={() => update({ status: s })}
                                        data-testid={`drawer-status-${s}`}
                                        className="rounded-md border border-border bg-card px-3 py-1.5 text-[12px] capitalize hover:bg-muted/40">
                                    → {s.replace("_", " ")}
                                </button>
                            ))}
                            {nextStates.length === 0 && (
                                <div className="text-[12px] text-muted-foreground">Task is complete.</div>
                            )}
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                            <div>Started<br /><span className="text-foreground">{t.started_at ? formatDateTime(t.started_at) : "—"}</span></div>
                            <div>Inspected<br /><span className="text-foreground">{t.inspected_at ? formatDateTime(t.inspected_at) : "—"}</span></div>
                            <div>Completed<br /><span className="text-foreground">{t.completed_at ? formatDateTime(t.completed_at) : "—"}</span></div>
                        </div>
                    </div>

                    {/* Assignment + priority */}
                    {!isHK && (
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Assigned to">
                                <select className="akm-input" value={t.assigned_to || ""}
                                        onChange={(e) => update({ assigned_to: e.target.value || null })}
                                        data-testid="drawer-assign">
                                    <option value="">Unassigned</option>
                                    {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </Field>
                            <Field label="Priority">
                                <select className="akm-input" value={t.priority}
                                        onChange={(e) => update({ priority: e.target.value })}
                                        data-testid="drawer-priority">
                                    <option value="low">Low</option>
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                </select>
                            </Field>
                        </div>
                    )}

                    {/* Notes */}
                    <Field label="Notes">
                        <textarea className="akm-input" rows="3" value={t.notes || ""}
                                  onChange={(e) => setT({ ...t, notes: e.target.value })}
                                  onBlur={() => t.notes !== task.notes && update({ notes: t.notes || "" })}
                                  data-testid="drawer-notes"
                                  placeholder="Add notes…" />
                    </Field>

                    {/* Photos */}
                    <div>
                        <div className="mb-1 flex items-center justify-between">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                Photos {t.photos?.length ? `(${t.photos.length}/5)` : ""}
                            </div>
                            <button onClick={() => fileRef.current?.click()} disabled={uploading || (t.photos?.length || 0) >= 5}
                                    className="akm-btn-ghost text-[11px]" data-testid="drawer-upload-photo">
                                <Camera size={11} /> {uploading ? "Uploading…" : "Add photo"}
                            </button>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden"
                                   onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                        </div>
                        {t.photos?.length > 0 ? (
                            <div className="grid grid-cols-3 gap-2">
                                {t.photos.map((p, i) => (
                                    <div key={i} className="relative">
                                        <img src={p} alt="" className="h-24 w-full rounded-md object-cover" />
                                        <button onClick={() => removePhoto(i)}
                                                className="absolute right-1 top-1 rounded-full bg-background/90 p-1 text-red-700 hover:bg-background"
                                                data-testid={`drawer-photo-remove-${i}`}>
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-md border border-dashed border-border bg-card/40 py-6 text-center text-[11px] text-muted-foreground">
                                No photos yet. Use the camera to document issues.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Task create form (manager only)
// ------------------------------------------------------------
function TaskForm({ rooms, properties, staff, onClose, onSaved }) {
    const [form, setForm] = useState({
        property_id: properties[0]?.id || "",
        room_id: rooms[0]?.id || "",
        task_type: "cleaning",
        priority: "normal",
        assigned_to: "",
        notes: "",
        due_date: new Date().toISOString().slice(0, 10),
    });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = { ...form };
            if (!body.assigned_to) delete body.assigned_to;
            await api.post("/housekeeping/tasks", body);
            toast.success("Task created");
            onSaved();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
        finally { setSaving(false); }
    };
    const filteredRooms = rooms.filter((r) => r.property_id === form.property_id);
    return (
        <Modal title="New housekeeping task" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="hk-task-form">
                <Field label="Property">
                    <select className="akm-input" value={form.property_id}
                            onChange={(e) => setForm({ ...form, property_id: e.target.value, room_id: rooms.find((r) => r.property_id === e.target.value)?.id || "" })}>
                        {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </Field>
                <Field label="Room">
                    <select className="akm-input" value={form.room_id}
                            onChange={(e) => setForm({ ...form, room_id: e.target.value })}>
                        {filteredRooms.map((r) => <option key={r.id} value={r.id}>{r.number} · {r.room_type} · {r.status}</option>)}
                    </select>
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Type">
                        <select className="akm-input" value={form.task_type}
                                onChange={(e) => setForm({ ...form, task_type: e.target.value })}>
                            {["cleaning", "inspection", "maintenance", "linen_change"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Priority">
                        <select className="akm-input" value={form.priority}
                                onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                            {["low", "normal", "high"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                    </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Assign to">
                        <select className="akm-input" value={form.assigned_to}
                                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                                data-testid="form-assign">
                            <option value="">Unassigned</option>
                            {staff.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
                        </select>
                    </Field>
                    <Field label="Due date">
                        <input type="date" className="akm-input" value={form.due_date}
                               onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                    </Field>
                </div>
                <Field label="Notes">
                    <textarea className="akm-input" rows="2" value={form.notes}
                              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="form-submit">
                        {saving ? "Saving…" : "Create"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
